import React from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import MainLayout from '../layouts/MainLayout';
import ErrorLayout from '../layouts/ErrorLayout';
import Error403 from 'modules/errors/Error403';
import Error404 from 'modules/errors/Error404';
import Error500 from 'modules/errors/Error500';
import Login from 'modules/user_login/Login';
import InputOTPPage from 'modules/user_login/LoginWithZoho';
import ResetPassword from 'modules/user_login/ResetPassword';
import OfflineIframeLogin from 'modules/user_login/OfflineIframeLogin';
import RequireAuth from 'components/RequireAuth';
import { homeRoute } from 'modules/utilities/utility';
import useDynamicRoutes from './useDynamicRoutes';
import Cookies from 'js-cookie';

const UmamiFunnelDashboard = React.lazy(() => import('modules/umami_funnel_dashboard'));

/** Redirect /lead-details/:id → /lead-details-v2/:id */
const LeadDetailsRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/lead-details-v2/${id}`} replace />;
};

/** Full-page loading spinner shown while permissions are being fetched */
const LoadingPage = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" />
  </div>
);

/** Captures the attempted path before redirecting to 404 */
const NotFoundRedirect = () => {
  const location = useLocation();
  return <Navigate to="/errors/404" replace state={{ attemptedPath: location.pathname + location.search }} />;
};

const CRMRoutes = () => {
  const token = Cookies.get('token');
  const { routes: dynamicRoutes, isLoading: permissionsLoading } = useDynamicRoutes();

  return (
    <Routes>
      {/* === Error pages (unauthenticated fallback) === */}
      <Route element={<ErrorLayout />}>
        <Route path='errors/500' element={<Error500 />} />
      </Route>

      {/* === Public routes (no auth required) === */}
      <Route path="login" element={<Login />} />
      <Route path="offline-iframe-login" element={<OfflineIframeLogin />} />
      <Route path="reset-password/:id" element={<ResetPassword />} />
      <Route path="/loginWithOtp" element={<InputOTPPage />} />

      {/* === Authenticated routes === */}
      {!token ? (
        <>
          <Route path="*" element={<Login />} />
        </>
      ) : (
        <Route path="/" element={<RequireAuth><MainLayout /></RequireAuth>}>
          {/* Home redirect */}
          <Route path="/" element={<Navigate to={homeRoute()} replace />} />

          {/* Legacy redirect routes — old bookmarks/links still work */}
          <Route path='/discount-codes' element={<Navigate to='/configure/discount-codes' replace />} />
          <Route path='/rbac-configure' element={<Navigate to='/configure/rbac-management' replace />} />
          <Route path='/ticket-group-management' element={<Navigate to='/configure/ticket-group-management' replace />} />
          <Route path='/clinic-management' element={<Navigate to='/configure/clinic-management' replace />} />
          <Route path='/offline-doctor-roaster' element={<Navigate to='/configure/offline-doctor-roaster' replace />} />
          <Route path='/webinar-management' element={<Navigate to='/configure/webinar-management' replace />} />
          <Route path='/configure-ticket' element={<Navigate to='/configure/ticket-configure' replace />} />
          <Route path='/slot-booking-total' element={<Navigate to='/engagement-report?tab=slot_booking_total' replace />} />

          {/* Redirect lead-details to v2 */}
          <Route path='/lead-details/:id' element={<LeadDetailsRedirect />} />

          {/* Legacy path redirects — old camelCase/underscore/singular paths → correct paths */}
          <Route path='/performance_dashboard' element={<Navigate to='/performance-dashboard' replace />} />
          <Route path='/configure/medicine' element={<Navigate to='/configure/medicines' replace />} />
          <Route path='/configure/productMapping' element={<Navigate to='/configure/product-mapping' replace />} />

          {/* Static route for Umami Analytics */}
          <Route path="funnel-analytics" element={<React.Suspense fallback={<Spin size="large" />}><UmamiFunnelDashboard /></React.Suspense>} />

          {/* ALL functional routes from dynamic router (RBAC-based) */}
          {dynamicRoutes && dynamicRoutes.length > 0 && dynamicRoutes}

          {/* Error pages inside MainLayout so sidebar stays visible */}
          <Route path='errors/403' element={<Error403 />} />
          <Route path='errors/404' element={<Error404 />} />
        </Route>
      )}

      {/* === Catch-all === */}
      {token ? (
        permissionsLoading
          ? <Route path="*" element={<LoadingPage />} />
          : <Route path="*" element={<NotFoundRedirect />} />
      ) : (
        <Route path="*" element={<Login />} />
      )}
    </Routes>
  );
};

export default CRMRoutes;
